import type { ComponentProps } from "./article.d";
import "./article.scss";

import type { Content } from "@enonic-types/lib-content/content.d";
import React, { useCallback, useEffect, useState } from "react";
import { BlueCardTheme, defaultCardTheme, whiteCardTheme } from "../../../react4xp/shared/components/article/index";
import Pagination from "../../../react4xp/shared/components/pagination";
import { useAppStore } from "../../../react4xp/shared/stores";
import { getCookie, http, setCookie } from "../../../react4xp/shared/utils/client";
import type { ArticleData, ArticleDataList } from "../../content-types/article/article.d";

function Component(props: ComponentProps) {
	const { tabsType, group, categories } = props;

	const [page, setPage] = useState<number>(1);
	const [category, setCategory] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [items, setItems] = useState<ArticleDataList | null>(null);
	const [iframeHtml, setIframeHtml] = useState<string>("");

	const appStore = useAppStore((state) => state);
	useEffect(() => {
		if (!items?.hits[0]?.data.content) return;

		const table = document.querySelector<HTMLTableElement>(".tab-pane.show.active table.projects-table");
		const downloadLink = document.querySelector<HTMLAnchorElement>(".tab-pane.show.active a.download-csv");

		if (!table || !downloadLink) return;

		const tableToCSV = (table: HTMLTableElement) => {
			const rows = table.querySelectorAll("tr");
			const csv: string[] = [];

			rows.forEach((row) => {
				const cols = row.querySelectorAll("td, th");
				const rowData = Array.from(cols).map((col) => `"${col.textContent?.replace(/"/g, '""').trim()}"`);
				csv.push(rowData.join(","));
			});

			return csv.join("\n");
		};

		const handleDownload = (e: Event) => {
			e.preventDefault();
			const csv = tableToCSV(table);
			const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = "projects.csv";
			a.click();
			URL.revokeObjectURL(url);
		};

		downloadLink.addEventListener("click", handleDownload);

		return () => {
			downloadLink.removeEventListener("click", handleDownload);
		};
	}, [items]);

	useEffect(() => {
		if (categories?.hits.length > 0 && !category) {
			const params = new URLSearchParams(window.location.search);
			const categoryFromUrl = params.get("category");

			if (categoryFromUrl && categories.hits.some((c) => c._id === categoryFromUrl)) {
				setCategory(categoryFromUrl);
			} else {
				setCategory(categories.hits[0]._id);
			}
		}
	}, [categories, category]);

	useEffect(() => {
		if (typeof window !== "undefined" && category) {
			const url = new URL(window.location.href);
			url.searchParams.set("category", category);
			window.history.replaceState({}, "", url.toString());
		}
	}, [category]);

	const fetchItemsByCategory = useCallback(async () => {
		if (appStore.sitePath == null || appStore.appName == null || !category) return;

		setItems(null);
		setLoading(true);

		const response = await http({
			url: `api/article?category=${category}&page=${page}`,
		});

		setItems(response.jsonData as ArticleDataList | null);
		setLoading(false);
	}, [appStore.sitePath, appStore.appName, category, page]);

	const incrementItemViews = useCallback(
		(item: Content<ArticleData>) => {
			if (appStore.sitePath == null || appStore.appName == null || item == null) return;

			http({
				url: "api/article?incrementViewed",
				method: "PUT",
				body: JSON.stringify({ _id: item._id }),
			});
		},
		[appStore.sitePath, appStore.appName]
	);

	const handlePageChange = (page: number) => {
		setPage(page);
	};

	const handleCategoryChange = (newCategoryId: string) => {
		if (newCategoryId !== category) {
			setCategory(newCategoryId);
			setPage(1);
		}
	};
	useEffect(() => {
		if (category) {
			fetchItemsByCategory();
		}
	}, [category, page, fetchItemsByCategory]);

	useEffect(() => {
		if (page === 1 && items?.hits.length === 1) {
			const storageKey = `viewed_${items.hits[0]._id}`;
			if (!getCookie(storageKey)) {
				setCookie(storageKey, "true", 24);
				incrementItemViews(items.hits[0]);
			}
		}
	}, [page, items, incrementItemViews]);

	useEffect(() => {
		const content = items?.hits[0]?.data?.content;

		if (content) {
			const decodedContent = content.replace(/&lt;/g, "<").replace(/&gt;/g, ">");

			const iframeMatch = decodedContent.match(/<iframe[^>]*>[^<]*<\/iframe>/);
			if (iframeMatch && iframeMatch[0]) {
				const cleanedIframeHtml = iframeMatch[0].replace(/\\"/g, '"');
				setIframeHtml(cleanedIframeHtml);
			}
		}
	}, [items?.hits]);

	const accordionItems = Array.isArray(items?.hits[0].data.accordionItems)
		? items?.hits[0].data.accordionItems
		: items?.hits[0].data.accordionItems
			? [items.hits[0].data.accordionItems]
			: [];

	const links = Array.isArray(items?.hits[0].data.linkContent)
		? items?.hits[0].data.linkContent
		: items?.hits[0].data.linkContent
			? [items?.hits[0].data.linkContent]
			: [];

	return (
		<section className={`article article-${group}`}>
			{categories.hits.length > 0 ? (
				<div className={`d-md-flex gap-3 ${tabsType === "vertical" ? "" : "flex-column"}`}>
					<div
						className={`nav nav-pills gap-2 mb-3 mb-md-0 ${tabsType === "vertical" ? "flex-column" : ""}`}
						id={`article-${group}-tab`}
					>
						{categories.hits.map(({ _id, displayName }, i) => (
							<button
								key={`article-${group}-tab-${_id}`}
								type="button"
								className={`btn nav-link article-btn ${_id === category ? "active" : ""}`}
								onClick={() => handleCategoryChange(_id)}
								id={`article-${group}-tab-${i}`}
								data-bs-toggle="pill"
								data-bs-target={`#article-${group}-content-${i}`}
							>
								{displayName}
								<i className={`fa-solid fa-chevron-${tabsType === "vertical" ? "right" : "down"}`}></i>
							</button>
						))}
					</div>

					<div className="tab-content w-100" id={`article-${group}-tabContent`}>
						{categories.hits.map((item, i) => (
							<div
								key={`article-${group}-content-${item._id}`}
								className={`tab-pane fade w-100 h-100 ${item._id === category ? "show active" : ""} `}
								id={`article-${group}-content-${i}`}
							>
								{(item.data.cardTheme === "white-cards" || item.data.cardTheme === "blue-cards") && (
									<h3 className="article-title text-center">{item.displayName}</h3>
								)}
								{items?.hits.length ? (
									items?.hits.length > 1 || page > 1 ? (
										<>
											<div
												className={
													item.data.cardTheme === "blue-cards" || item.data.cardTheme === "white-cards"
														? "blue-cards-grid"
														: ""
												}
											>
												{items?.hits.map(
													({
														_id,
														displayName,
														modifiedTime,
														publish,
														data: {
															contentTypeLink,
															contentTypeImage,
															contentTypeImageAltText,
															description,
															viewed,
															linkContent,
															projectStatus,
														},
													}) =>
														item.data.cardTheme === "blue-cards" ? (
															<BlueCardTheme
																key={_id}
																_id={_id}
																contentTypeLink={contentTypeLink}
																displayName={displayName}
																description={description}
																projectStatus={projectStatus}
															/>
														) : item.data.cardTheme === "white-cards" ? (
															whiteCardTheme({
																_id,
																contentTypeLink,
																displayName,
																description,
																contentTypeImage,
																contentTypeImageAltText,
																linkContent,
															})
														) : (
															defaultCardTheme({
																_id,
																contentTypeLink,
																displayName,
																description,
																contentTypeImage,
																contentTypeImageAltText,
																modifiedTime,
																publish,
																viewed,
															})
														)
												)}
											</div>
											<div className="pagination-container">
												<Pagination total={items.total} currentPage={page} onPageChange={handlePageChange} />
											</div>
										</>
									) : (
										<div className="d-flex flex-column gap-2 mx-0 mx-md-2">
											<div className="info text-center">
												<a href={items.hits[0].data.contentTypeLink} className="btn p-0">
													<h1 className="article-title">{items.hits[0].displayName}</h1>
												</a>
											</div>

											<img
												src={items.hits[0].data.contentTypeImage}
												alt={items.hits[0].data.contentTypeImageAltText}
												loading="lazy"
												className="w-100"
											/>

											<div className="mt-2">
												<div
													dangerouslySetInnerHTML={{
														__html: items.hits[0].data.content,
													}}
												></div>
												{iframeHtml && <div dangerouslySetInnerHTML={{ __html: iframeHtml }} />}
											</div>

											{accordionItems.length > 0 && (
												<div className="accordion" id={`accordion-${items.hits[0]._id}`}>
													{accordionItems.map((accordionItem, index) => (
														<>
															<div>
																{accordionItem.MainTitle && (
																	<h3 className="accordion-main-title">{accordionItem.MainTitle}</h3>
																)}
															</div>
															<div className="accordion-item" key={`accordion-item-${index}`}>
																<h2 className="accordion-header">
																	<button
																		className="accordion-button"
																		type="button"
																		data-bs-toggle="collapse"
																		data-bs-target={`#collapse-${items.hits[0]._id}-${index}`}
																		aria-expanded={index === 0 ? "true" : "false"}
																		aria-controls={`collapse-${items.hits[0]._id}-${index}`}
																	>
																		{items.hits[0].data.showNumbering && <div className="item-number">{index + 1}</div>}
																		<div dangerouslySetInnerHTML={{ __html: accordionItem.panelTitle }} />
																	</button>
																</h2>
																<div
																	id={`collapse-${items.hits[0]._id}-${index}`}
																	className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`}
																	data-bs-parent={`#accordion-${items.hits[0]._id}`}
																>
																	<div className="accordion-body">
																		<div dangerouslySetInnerHTML={{ __html: accordionItem.panelContent }} />
																	</div>
																</div>
															</div>
														</>
													))}
												</div>
											)}
											{links.length > 0 && <h5 className="m-0">{appStore.phrases?.["helpfulLinks"]}</h5>}
											{links.map((link, index) => (
												<a
													key={`article-link-${index}`}
													href={link.linkUrl}
													className="white-card-link"
													target="_blank"
													rel="noopener noreferrer"
												>
													<i className="fa-solid fa-up-right-from-square me-2"></i>
													{link.linkTitle}
												</a>
											))}
										</div>
									)
								) : (
									<div className="empty">
										{loading ? (
											<div className="spinner-border text-primary"></div>
										) : (
											<h3>{appStore.phrases?.["empty"]}</h3>
										)}
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			) : (
				<div className="empty">
					<h3>{appStore.phrases?.["empty"]}</h3>
				</div>
			)}
		</section>
	);
}

export default function WrappedComponent(props: ComponentProps) {
	return <Component {...props} />;
}
